import { Link } from 'react-router-dom';
import HeroCarousel from './components/HeroCarousel';
import SectionHeading from '@/components/base/SectionHeading';
import MovieCard from '@/components/base/MovieCard';
import { useSeo } from '@/hooks/useSeo';
import { allGenres, getHotMovies, getNewMovies } from '@/utils/movies';

export default function Home() {
  useSeo({
    title: '6080yy电影在线看 - 高清电影在线播放平台',
    description:
      '6080yy电影在线看为您提供丰富多样的高清电影资源，包括最新热门电影和经典影片，支持流畅在线播放，满足您的观影需求。',
    keywords: '6080yy电影在线看,在线观看电影,高清电影,在线电影平台,电影播放,电影资源',
  });

  const hotMovies = getHotMovies(5);
  const rankMovies = getHotMovies(10);
  const newMovies = getNewMovies(8);

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
          <div>
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
              6080yy电影在线看
            </h1>
            <p className="mt-1 text-sm md:text-base text-foreground-500">
              高清电影在线播放平台 · 汇聚最新热门影片与经典佳作
            </p>
          </div>
          <Link
            to="/category/action"
            className="hidden sm:inline-flex items-center gap-1 text-sm text-accent-600 hover:text-accent-700 cursor-pointer whitespace-nowrap"
          >
            浏览全部分类
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>
        <HeroCarousel movies={hotMovies} />
      </div>

      <div className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="mb-5">
          <SectionHeading
            icon="ri-apps-2-line"
            title="电影分类"
            subtitle="按类型快速找到你想看的影片"
            action={
              <Link
                to="/category/action"
                className="hidden sm:flex items-center gap-1 text-sm text-accent-600 hover:text-accent-700 cursor-pointer whitespace-nowrap"
              >
                全部分类
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            }
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
          {allGenres.map((genre) => (
            <Link
              key={genre.key}
              to={`/category/${genre.key}`}
              className="group flex flex-col items-center text-center gap-2 p-4 md:p-5 rounded-lg bg-background-50 border border-background-200 hover:border-accent-400 transition-colors cursor-pointer"
            >
              <span className="w-12 h-12 md:w-14 md:h-14 rounded-md bg-primary-500 flex items-center justify-center group-hover:bg-accent-500 transition-colors">
                <i className={`${genre.icon} text-background-50 group-hover:text-primary-950 text-2xl w-6 h-6 flex items-center justify-center transition-colors`}></i>
              </span>
              <span className="font-heading font-bold text-sm md:text-base text-foreground-950">{genre.name}</span>
              <span className="hidden md:block text-xs text-foreground-500 leading-relaxed line-clamp-2">{genre.desc}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="w-full px-4 md:px-6 pb-10 md:pb-14">
        <SectionHeading
          icon="ri-fire-line"
          title="热门电影"
          subtitle="当前最受影迷关注的高分影片"
          action={
            <Link
              to="/category/drama"
              className="hidden sm:flex items-center gap-1 text-sm text-accent-600 hover:text-accent-700 cursor-pointer whitespace-nowrap"
            >
              查看更多
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          }
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {rankMovies.map((movie, i) => (
            <MovieCard key={movie.id} movie={movie} rank={i + 1} />
          ))}
        </div>
      </div>

      <div className="w-full bg-primary-600">
        <div className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-md bg-background-50 flex items-center justify-center shrink-0">
              <i className="ri-movie-2-line text-primary-600 text-xl w-5 h-5 flex items-center justify-center"></i>
            </span>
            <div>
              <h2 className="font-heading font-extrabold text-xl md:text-2xl text-background-50">新片推荐专题</h2>
              <p className="text-sm text-background-200/80 mt-0.5">近期上线的热门新片，抢鲜观看</p>
            </div>
          </div>
          <div className="flex gap-3 md:gap-4 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
            {newMovies.map((movie) => (
              <Link
                key={movie.id}
                to={`/detail/${movie.id}`}
                className="group shrink-0 w-[150px] md:w-[180px] cursor-pointer"
              >
                <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-primary-800">
                  <img
                    src={movie.poster}
                    alt={`${movie.title}（${movie.year}）新片海报`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-accent-500 text-primary-950 text-xs font-heading font-bold">
                    {movie.rating.toFixed(1)}
                  </span>
                  <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-primary-950/90 to-transparent">
                    <p className="text-background-50 text-sm font-heading font-bold truncate">{movie.title}</p>
                    <p className="text-background-200/80 text-xs">
                      {movie.year} · {movie.region}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-6">
            <Link
              to="/category/scifi"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 font-label font-semibold text-sm hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看完整片单
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center bg-background-100 rounded-lg border border-background-200 p-6 md:p-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-3">
              关于 6080yy电影在线看
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              6080yy电影在线看专注于为影迷打造清爽、易用的高清电影内容平台。我们汇集动作、科幻、爱情、悬疑、动画等多种类型的优质影片，
              持续更新最新热门电影与经典佳作，帮助你用最简单的方式发现并了解每一部值得一看的作品。
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-lg bg-background-50 border border-background-200">
              <p className="font-heading font-extrabold text-xl text-primary-600">20+</p>
              <p className="text-xs text-foreground-500 mt-1">示范影片</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-background-50 border border-background-200">
              <p className="font-heading font-extrabold text-xl text-primary-600">10</p>
              <p className="text-xs text-foreground-500 mt-1">影片类型</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-background-50 border border-background-200">
              <p className="font-heading font-extrabold text-xl text-accent-600">每日</p>
              <p className="text-xs text-foreground-500 mt-1">持续更新</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}